import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

const EDITIONS = [
  {
    num: "01", edition: "R&B", subtitle: "Original Master",
    cover: "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/kd42ok0u_FosterCareAnthem_WereComingForYou_2026.png",
    quote: "Smooth, soulful — the prayer voice of the project.",
    body: "The tenderness of a vow whispered over a sleeping child.",
  },
  {
    num: "02", edition: "Hip-Hop", subtitle: "Street Edition",
    cover: "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/gfc6slcn_FosterCareAnthem_Cover_4.png",
    quote: "Gritty, urgent, street-level.",
    body: "The anthem from the alleys and warehouses where the rescues happen.",
  },
  {
    num: "03", edition: "Gospel", subtitle: "Sanctuary Cry",
    cover: "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/e7j7p9pk_FosterCareAnthem_Cover_5.png",
    quote: "The sanctuary cry.",
    body: "A choir-driven battle prayer that calls down heaven on the darkness.",
  },
];

export default function WereComingForYou() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="wcfy-page">
      <SEO
        path="/anthem/were-coming-for-you"
        title="We're Coming For You | Foster Care Anthem · Trafficking Awareness"
        description="A direct address to children trapped in human trafficking — and a vow to those fighting to bring them home. One single, three visual editions: R&B (Original), Hip-Hop, and Gospel."
        image="https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/kd42ok0u_FosterCareAnthem_WereComingForYou_2026.png"
      />
      <section className="border-b border-an-border">
        <div className="container-xl pt-24 pb-16">
          <div className="overline text-an-gold mb-5">Trafficking Awareness Series · April 30 Launch</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.88]">We're Coming <br/><span className="italic">For You.</span></h1>
          <p className="mt-8 text-an-muted text-lg max-w-3xl leading-relaxed">
            A direct address to children trapped in human trafficking — and a vow to those fighting to bring them home. One single, three visual editions: R&B (Original), Hip-Hop, and Gospel.
          </p>
          <div className="mt-8 flex gap-4 flex-wrap">
            <Link to="/anthem/licensing" className="btn-primary" data-testid="wcfy-license">License the Single</Link>
            <Link to="/anthem/catalog" className="btn-outline" data-testid="wcfy-catalog">Back to Catalog</Link>
          </div>
        </div>
      </section>

      <section className="border-b border-an-border">
        <div className="container-xl py-20">
          <div className="overline text-an-muted mb-8">Two Videos · One Single · Anthem Rising</div>
          <div className="grid md:grid-cols-2 gap-6 mb-14">
            <div className="border border-an-border bg-an-surface" data-testid="wcfy-video-original">
              <div className="aspect-video overflow-hidden">
                <iframe className="w-full h-full" src="https://www.youtube.com/embed/3b6DNPr5bJI?rel=0" title="We're Coming For You — Original" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              <div className="p-5 flex items-baseline justify-between">
                <div>
                  <div className="overline text-an-gold mb-1">/ 01</div>
                  <div className="font-display text-xl">Original Version</div>
                </div>
                <a href="https://youtu.be/3b6DNPr5bJI" target="_blank" rel="noopener noreferrer" className="overline link-ul">YouTube →</a>
              </div>
            </div>
            <div className="border border-an-border bg-an-surface" data-testid="wcfy-video-rnb">
              <div className="aspect-video overflow-hidden">
                <iframe className="w-full h-full" src="https://www.youtube.com/embed/hBVCoG4Smfo?rel=0" title="We're Coming For You — R&B Version" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              <div className="p-5 flex items-baseline justify-between">
                <div>
                  <div className="overline text-an-gold mb-1">/ 02</div>
                  <div className="font-display text-xl">R&B Version</div>
                </div>
                <a href="https://youtu.be/hBVCoG4Smfo" target="_blank" rel="noopener noreferrer" className="overline link-ul">YouTube →</a>
              </div>
            </div>
          </div>

          <div className="overline text-an-muted mb-8">Three Visual Editions · One Song · Three Voices</div>
          <div className="grid md:grid-cols-3 gap-6">
            {EDITIONS.map((e) => (
              <div key={e.num} className="border border-an-border bg-an-surface card-hover" data-testid={`wcfy-edition-${e.edition.toLowerCase()}`}>
                <div className="aspect-square border-b border-an-border overflow-hidden">
                  <img src={e.cover} alt={e.edition} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="font-display text-3xl">{e.edition}</div>
                    <div className="font-mono text-xs text-an-gold">/{e.num}</div>
                  </div>
                  <div className="overline text-an-muted mb-4">{e.subtitle}</div>
                  <blockquote className="font-display italic text-lg leading-tight mb-4">"{e.quote}"</blockquote>
                  <p className="text-sm text-an-muted leading-relaxed">{e.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="container-xl py-24 max-w-4xl">
          <div className="overline text-an-gold mb-4">The Vow</div>
          <blockquote className="font-display text-3xl md:text-5xl italic leading-tight">
            "If we don't do it, who will?"
          </blockquote>
          <p className="mt-8 text-an-muted leading-relaxed">
            Every edition is engineered as a drop-in advocacy tool for anti-trafficking campaigns, recovery ministries, and organizations fighting for children still in the dark.
          </p>
        </div>
      </section>
    </div>
  );
}
